import React, { useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { shipmentPinLatLng } from './portCoords';
import { useTheme } from '../../../context/ThemeContext';
import { shipmentHeading } from '../../../utils/shipHeading';
import { isHubWaiting, currentVesselName } from '../../../utils/hubWait';
import { STATUS_COLORS, STATUS_STROKES } from '../map/modeIcons';
import {
  asVoyageStops,
  maxVoyageDelay,
  voyageHasDelay,
  voyageIsAhead,
} from '../../../utils/voyageDelay';

const COMMODITY_COLORS = {
  'Grapes': '#7c3aed',
  'Blueberries': '#3b82f6',
  'Avocados': '#16a34a',
  'Citrus': '#f97316',
  'Mangoes': '#eab308',
  'Cherries': '#dc2626',
  'Asparagus': '#059669',
  'Pomegranates': '#be185d',
};

// The hull reads its colours from the shared status palette rather than its own
// copy, so a vessel, a plane and the map legend can never describe the same
// state three different ways.
const DELAY_RED = STATUS_COLORS.delayed;
const DELAY_STROKE = STATUS_STROKES.delayed;
const AHEAD_FILL = STATUS_COLORS.ahead;
const AHEAD_STROKE = STATUS_STROKES.ahead;
const ON_TIME_FILL = STATUS_COLORS.on_time;

const getCommodityColor = (commodities) => {
  if (!commodities) return '#22c55e';
  const first = commodities.split(',')[0].trim();
  for (const [key, color] of Object.entries(COMMODITY_COLORS)) {
    if (first.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#22c55e';
};


export { isHubWaiting, currentVesselName };
// The delay derivation moved to utils/voyageDelay so the Shipment status chip
// can share it without pulling in Leaflet; re-exported here under the names
// every caller already imports from this module.
export { asVoyageStops, maxVoyageDelay, voyageHasDelay, voyageIsAhead };

export function nextVesselName(shipment) {
  const next = shipment?.next_vessel;
  if (next == null || next === '') return '';
  const cur = currentVesselName(shipment);
  if (cur && String(next) === String(cur)) return '';
  return String(next);
}

/** Live ShipsGo ship when it differs from API vessel (booking after BE stop-stamping). */
export function liveVesselName(shipment) {
  const live = shipment?.current_vessel;
  if (live == null || live === '') return '';
  const booked = shipment?.vessel;
  if (booked && String(live) === String(booked)) return '';
  return String(live);
}

/** API voyage_stops only. Keep this row's arrival_port (or same shipsgo_shipment_id). Never merge other PODs by IMO. */
export function trustedVoyageStops(shipment) {
  const stops = asVoyageStops(shipment?.voyage_stops);
  const pods = [...new Set(stops.map((s) => String(s?.arrival_port || '').toUpperCase().trim()).filter(Boolean))];
  if (pods.length > 1) return stops;
  const pod = String(shipment?.arrival_port || '').toUpperCase().trim();
  if (!pod) return stops.slice(0, 1);
  const samePod = stops.filter((s) => String(s?.arrival_port || '').toUpperCase().trim() === pod);
  if (samePod.length) return samePod;
  const sid = shipment?.shipsgo_shipment_id;
  if (sid != null && sid !== '') {
    const sameId = stops.filter((s) => String(s?.shipsgo_shipment_id || '') === String(sid));
    if (sameId.length) return sameId;
  }
  return stops.slice(0, 1);
}

const createShipIcon = (rotation = 0, selected = false, delayed = false, ahead = false) => {
  // Same footprint as prod/dev (selected 58 / default 43). Hit-testing is ShipClickResolver, not icon size.
  const size = selected ? 58 : 43;
  const half = size / 2;
  const stroke = delayed ? DELAY_STROKE : ahead ? AHEAD_STROKE : ON_TIME_FILL;
  const fill = delayed ? DELAY_RED : ahead ? AHEAD_FILL : '#ffffff';
  const ringColor = delayed ? DELAY_RED : ahead ? AHEAD_FILL : ON_TIME_FILL;
  const deg = Number.isFinite(rotation) ? rotation : 0;
  const svg = selected
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 48 48" style="display:block;--pin-glow:${ringColor};">
        <g transform="rotate(${deg} 24 24)">
          <circle cx="24" cy="24" r="22" fill="none" stroke="${ringColor}" stroke-width="3" opacity="0.8"/>
          <circle cx="24" cy="24" r="18" fill="none" stroke="${ringColor}" stroke-width="1.5" opacity="0.4"/>
          <path d="M24 12 L18 30 L24 26 L30 30 Z" fill="${fill}" stroke="${stroke}" stroke-width="2" stroke-linejoin="round"/>
        </g>
      </svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" style="display:block;--pin-glow:${stroke};">
        <g transform="rotate(${deg} 12 12)">
          <path d="M12 2 L8 10 L12 8 L16 10 Z" fill="${fill}" stroke="${stroke}" stroke-width="1.5" stroke-linejoin="round"/>
        </g>
      </svg>`;
  return L.divIcon({
    html: svg,
    className: delayed
      ? `ship-marker ship-marker--delayed ship-marker--d${size}`
      : ahead
        ? `ship-marker ship-marker--ahead ship-marker--a${size}`
        : 'ship-marker',
    iconSize: [size, size],
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
  });
};

const HUB_AMBER = '#F59E0B';

// Non-rotating box glyph, same footprint as the ship icon so hit-testing is unchanged.
const createHubIcon = (selected = false, isDark = false) => {
  // The two exports differ only in the box's outline — white reads against
  // light tiles, near-black against dark ones.
  const src = isDark ? '/assets/icons/box-dark.svg' : '/assets/icons/box-light.svg';
  const size = selected ? 58 : 43;
  const half = size / 2;
  const svg = selected
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 48 48" style="display:block;--pin-glow:${HUB_AMBER};">
        <circle cx="24" cy="24" r="22" fill="none" stroke="${HUB_AMBER}" stroke-width="3" opacity="0.8"/>
        <circle cx="24" cy="24" r="18" fill="none" stroke="${HUB_AMBER}" stroke-width="1.5" opacity="0.4"/>
        <image href="${src}" x="10" y="10" width="28" height="28"/>
      </svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" style="display:block;--pin-glow:${HUB_AMBER};"><image href="${src}" x="2" y="2" width="20" height="20"/></svg>`;
  return L.divIcon({
    html: svg,
    className: 'ship-marker ship-marker--hub',
    iconSize: [size, size],
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
  });
};

const DELAYED_Z = 1200000;
const AHEAD_Z = 400000;
const SELECTED_Z = 200000;

const ShipMarker = ({ shipment, onClick, isSelected = false, isHovered = false, position = null, zIndexOffset = null, interactive = false, statusKey = null }) => {
  const { isDark } = useTheme();
  const pin = position || shipmentPinLatLng(shipment);
  const latitude = pin?.[0];
  const longitude = pin?.[1];
  const hub = isHubWaiting(shipment);
  // statusKey wins when the caller supplies one. The caller holds the same
  // object the details panel does; this component is handed whatever row sits
  // behind the pin, and the two are not always the same - which is how a
  // vessel badged "Ahead" kept drawing in the on-time blue. Deriving it here
  // from a poorer object was the bug, so the derivation is now the fallback.
  const delayed = !hub && (statusKey ? statusKey === 'delayed' : voyageHasDelay(shipment));
  const ahead = !hub && !delayed && (statusKey ? statusKey === 'ahead' : voyageIsAhead(shipment));
  const delayKey = maxVoyageDelay(shipment);
  const heading = useMemo(
    () => shipmentHeading(shipment, pin),
    [shipment, pin],
  );
  const icon = useMemo(
    () => (hub ? createHubIcon(isSelected, isDark) : createShipIcon(heading, isSelected, delayed, ahead)),
    [hub, heading, isSelected, delayed, ahead, delayKey, isDark],
  );
  const markerRef = useRef(null);

  useEffect(() => {
    const layer = markerRef.current;
    if (layer && icon && typeof layer.setIcon === 'function') {
      layer.setIcon(icon);
    }
    if (layer && delayed && typeof layer.bringToFront === 'function') {
      layer.bringToFront();
    }
  }, [icon, delayed]);

  // Sea markers are `pointer-events: none` so ShipClickResolver can own the
  // picking, which also means CSS :hover never fires on them. The resolver
  // already knows what is hovered, so it drives the same scale+glow through a
  // class instead. Toggled on the live element rather than rebuilt into the
  // icon: replacing the node would restart the transition mid-flight.
  // `icon` is a dependency because setIcon swaps that element out.
  useEffect(() => {
    const el = markerRef.current?.getElement?.();
    if (!el) return undefined;
    el.classList.toggle('ship-marker--hover', Boolean(isHovered));
    return () => el.classList.remove('ship-marker--hover');
  }, [isHovered, icon]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return (
    <Marker
      ref={markerRef}
      position={[latitude, longitude]}
      icon={icon}
      title=""
      alt=""
      interactive={interactive}
      pane={delayed ? 'shipDelayed' : ahead ? 'shipAhead' : 'shipNormal'}
      zIndexOffset={zIndexOffset != null ? zIndexOffset : (delayed ? DELAYED_Z : ahead ? AHEAD_Z : isSelected ? SELECTED_Z : 0)}
      eventHandlers={interactive ? {
        click: () => onClick && onClick(shipment),
        touchend: (e) => {
          const touch = e.originalEvent?.changedTouches?.[0];
          const startPos = e.target._touchStartPos;
          if (startPos && touch) {
            const dx = Math.abs(touch.clientX - startPos.x);
            const dy = Math.abs(touch.clientY - startPos.y);
            if (dx < 10 && dy < 10) {
              e.originalEvent.preventDefault();
              onClick && onClick(shipment);
            }
          } else {
            onClick && onClick(shipment);
          }
          delete e.target._touchStartPos;
        },
        touchstart: (e) => {
          const touch = e.originalEvent?.touches?.[0];
          if (touch) {
            e.target._touchStartPos = { x: touch.clientX, y: touch.clientY };
          }
        },
      } : undefined}
    />
  );
};

export { COMMODITY_COLORS, getCommodityColor };
export default ShipMarker;
